/**
 * TCL Stats — animated number counters.
 *
 * Tweened with GSAP (the theme's animation library; see docs/adr/0006). The
 * viewport trigger uses a native IntersectionObserver so we don't ship GSAP's
 * ScrollTrigger plugin just to fire a one-shot count. GSAP loads as a global
 * (window.gsap) via a deferred classic script in tcl-stats.liquid.
 *
 * Pure progressive enhancement: the final value for every stat is already
 * rendered server-side in Liquid, so this script only ever animates *toward* a
 * number that is already correct:
 *   - no JS                           → the final number is already on screen
 *   - GSAP missing (offline / error)  → final number stays on screen
 *   - prefers-reduced-motion: reduce  → snap to final, no animation
 * In every case there is zero layout shift.
 *
 * Section controls (data-* on the <section>): animate on/off, count direction
 * (up/down), duration (shared across cards), thousands grouping.
 * Per-stat controls (data-* on the number span): value (target), start value.
 */

(function () {
  'use strict';

  /* `sections/tcl-procedure-steps.liquid` reuses this counter for its duration
     figure, and `snippets/tcl-treatment-info-blocks.liquid` for its stat bars,
     so three sections can now request this file on one page — and a
     classic script with a repeated `src` RE-EXECUTES (the PR #60 GSAP bug). A
     second execution would re-bind every `[data-counter]` already on the page:
     two tweens writing the same element, and a `data-counted` guard that one of
     them has already set, so the visible number would fight itself. */
  if (window.__tclStatsLoaded) return;
  window.__tclStatsLoaded = true;

  /* Queried per call, not latched at load: macOS and iOS both apply a "reduce
     motion" change live, and a value read once at script evaluation keeps
     animating for the rest of the page's life. Every other script in this theme
     already does it this way. */
  function reduceMotion() {
    return !!(
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  // Digits after the decimal point in a value string, e.g. "98.50" -> 2.
  function decimalPlaces(str) {
    var match = String(str).match(/\.(\d+)/);
    return match ? match[1].length : 0;
  }

  // Tolerant numeric parse: "1,250" -> 1250, "98.5%" -> 98.5, "" -> NaN.
  function toNumber(str) {
    return parseFloat(String(str).replace(/[^0-9.\-]/g, ''));
  }

  /* Split a value into a numeric core and whatever brackets it, so a figure
     typed as "100%" can animate the 100 and keep the %.

     Returns null when the string is not a single number with optional affixes
     — "1 in 3", "2 - 3", "6-8 weeks", "24/7", "1 234" — because the affixes may
     not themselves contain digits. The caller then leaves the element alone
     rather than animating toward a number it cannot reproduce. (The old code
     stripped non-numerics and animated those to 13, 2, 68, 247 and 1,234
     respectively, silently replacing the merchant's text.) */
  var VALUE_RE = /^([^0-9.\-]*)(-?[\d,]*\.?\d+)([^0-9]*)$/;
  function splitValue(str) {
    var match = String(str).match(VALUE_RE);
    return match ? { prefix: match[1], core: match[2], suffix: match[3] } : null;
  }

  function bindStat(el, gsap, opts) {
    var parts = splitValue(el.dataset.value);
    if (!parts) return; // not reproducible — leave the server-rendered text as-is

    var to = toNumber(parts.core);
    if (!isFinite(to)) return;

    var decimals = decimalPlaces(parts.core);
    var hasStart = 'start' in el.dataset && el.dataset.start !== '';
    var from = hasStart ? toNumber(el.dataset.start) : opts.direction === 'down' ? to : 0;

    var formatter = new Intl.NumberFormat(undefined, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
      useGrouping: opts.group,
    });
    /* Echoing the parsed affixes is OPT-IN, via data-affix="auto".

       It has to be, because TCL Stats renders prefix and suffix as their own
       spans beside the number AND the real content has the unit typed into
       `value` as well — the homepage proof strip is value "100%" with suffix
       "%". Echoing the parsed "%" there prints it twice; that shipped to a
       signed-off page as "100%%" and was only caught on a real render.

       So: TCL Stats does not opt in and keeps rendering a bare number, exactly
       as before. tcl-treatment-info, whose `stat` block has ONE combined
       `figure` field, does. */
    var useAffix = el.dataset.affix === 'auto';
    var pre = useAffix ? parts.prefix : '';
    var suf = useAffix ? parts.suffix : '';
    function render(value) {
      el.textContent = pre + formatter.format(value) + suf;
    }

    // Nothing meaningful to animate (no GSAP, or from === to): keep final value.
    if (!gsap || !isFinite(from) || from === to) {
      render(to);
      return;
    }

    /* The server-rendered FINAL value is deliberately left in the DOM until the
       stat is actually about to animate.

       Priming with `from` at bind time instead — which this did — left every
       off-screen figure reading "0": find-in-page missed it, copy/paste yielded
       0, and anything reading the DOM ahead of the viewport (screen-reader
       virtual cursor, scraper, SEO crawler) saw 0 rather than the real number.
       Priming inside run() is visually identical, because run() only fires when
       the element is already at the viewport threshold. */

    // The active tween is held so we can kill it before re-running (replay) or
    // resetting — otherwise a stale tween keeps writing to render() and fights
    // the new one.
    var tween = null;

    function run() {
      if (el.dataset.counted === 'true') return; // already counting / counted
      el.dataset.counted = 'true';
      render(from); // prime, then immediately tween away from it
      var proxy = { value: from };
      tween = gsap.to(proxy, {
        value: to,
        duration: opts.duration,
        ease: 'power2.out',
        onUpdate: function () {
          render(proxy.value);
        },
      });
    }

    /* Re-arm so the count animates again next time the stat re-enters view.
       Restores the FINAL value, not `from`, for the same reason run() primes
       late: once the stat is off-screen its text should read the real number,
       not 0. run() re-primes on the way back in. */
    function reset() {
      if (tween) {
        tween.kill();
        tween = null;
      }
      el.dataset.counted = '';
      render(to);
    }

    if ('IntersectionObserver' in window) {
      // Keep observing (no unobserve): count on entry, re-arm on exit so the
      // animation replays every time the section leaves and re-enters view.
      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              run();
            } else {
              reset();
            }
          });
        },
        { threshold: 0.4 }
      );
      observer.observe(el);
    } else {
      run();
    }
  }

  /* Progress bars (tcl-treatment-info's stat blocks) fill in step with their
     figure. Without this the bar is full-width from load while the number
     counts up from zero, and the two visibly contradict each other for the
     whole tween.

     A CSS transition rather than a GSAP tween: it is one animated property, the
     final width is already server-rendered, and this keeps the bar working when
     GSAP is absent. Like the counters it re-arms on exit, so the two stay in
     step on replay. */
  function bindBars(section, animate, opts) {
    var bars = section.querySelectorAll('[data-bar]');
    if (!bars.length) return;

    bars.forEach(function (bar) {
      var pct = parseFloat(bar.dataset.bar);
      if (!isFinite(pct)) return;

      // No animation wanted, or nothing to trigger it: leave the server value.
      if (!animate || !('IntersectionObserver' in window)) return;

      bar.style.width = '0%';
      /* cubic-bezier(0.215, 0.61, 0.355, 1) is the CSS equivalent of GSAP's
         power2.out, which is what bindStat tweens the figure with. Matching the
         curve is what makes the pair read as one movement rather than two. */
      bar.style.transition =
        'width ' + opts.duration + 's cubic-bezier(0.215, 0.61, 0.355, 1)';

      new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            bar.style.width = (entry.isIntersecting ? pct : 0) + '%';
          });
        },
        { threshold: 0.4 }
      ).observe(bar);
    });
  }

  function initSection(section) {
    if (section.dataset.tclStatsBound === 'true') return;
    section.dataset.tclStatsBound = 'true';

    var counters = section.querySelectorAll('[data-counter]');
    if (!counters.length) return;

    var opts = {
      duration: parseFloat(section.dataset.duration) || 2,
      direction: section.dataset.direction === 'down' ? 'down' : 'up',
      group: section.dataset.group !== 'false',
    };

    var animateOn = section.dataset.animate !== 'false' && !reduceMotion();
    var gsap = animateOn && window.gsap ? window.gsap : null;

    counters.forEach(function (el) {
      bindStat(el, gsap, opts);
    });

    bindBars(section, animateOn, opts);
  }

  function init(root) {
    (root || document).querySelectorAll('[data-tcl-stats]').forEach(initSection);
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', function () { init(); });

  // Theme editor: re-init when a stats section is added or reloaded.
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (event) { init(event.target); });
  }
})();
